import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { POI } from "../MapCanvas";
import { findIconByName } from "../../data/fontawesome-icons";

interface ArrivalModalProps {
  destination: POI;
  onClose: () => void;
}

/**
 * Modal displayed when user arrives at their navigation destination.
 * Auto-dismisses after 5 seconds.
 */
export function ArrivalModal({ destination, onClose }: ArrivalModalProps) {
  const { t } = useTranslation("pois");
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const startTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(onClose, 5000);
  }, [onClose]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // Auto-dismiss after 5 seconds, pauses on hover/focus
  useEffect(() => {
    startTimer();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [startTimer]);

  const categoryIcon = destination.category?.icon
    ? findIconByName(destination.category.icon)
    : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-sm"
        onPointerEnter={pauseTimer}
        onPointerLeave={startTimer}
        onFocus={pauseTimer}
        onBlur={startTimer}
      >
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            {categoryIcon ? (
              <FontAwesomeIcon
                icon={categoryIcon.icon}
                className="h-8 w-8 text-green-600"
              />
            ) : (
              <span className="text-3xl">📍</span>
            )}
          </div>
          <DialogTitle className="text-xl">{t("arrival.title")}</DialogTitle>
          <DialogDescription className="text-base">
            {destination.name}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button onClick={onClose} className="w-full sm:w-auto">
            {t("common:done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
